import KeyboardArrowDown from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUp from "@material-ui/icons/KeyboardArrowUp";
import { useUser } from "@saleor/auth";
import { Zone } from "@saleor/auth/types";
import { FIELD_TYPE } from "@saleor/changeOrderExport/components/OrderFilterSection/constant";
import {
  InquiryOrderConfirmationStatus,
  PermissionEnum,
  useDistributionChannelsDomesticQuery,
  useDistributionChannelsExportQuery,
  useDropdownListMaterialGroup1Query,
  useExportSoldTosLazyQuery,
  useFilterDomesticOrderConfirmationCompanyQuery,
  useFilterRequireAttentionMaterialLazyQuery
} from "@saleor/graphql";
import { useFormik } from "formik";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { getProductGroup } from "./utils";

const useOrderFilterConfirmation = ({ handleChangeFilter }) => {
  const { listCompany, company, user, zone } = useUser();
  const intl = useIntl();
  const {
    data: confirmationConpanies
  } = useFilterDomesticOrderConfirmationCompanyQuery();
  const isCustomer = useMemo(
    () =>
      !!user?.userPermissions.find(
        e => e.code === PermissionEnum.ALL_REPORT_CUSTOMER
      ),
    [user?.userPermissions]
  );

  const { data: domesticChannels } = useDistributionChannelsDomesticQuery();
  const { data: exportChannels } = useDistributionChannelsExportQuery();
  const { data: productGroup } = useDropdownListMaterialGroup1Query({
    variables: { bu: "PP" }
  });

  const channels = useMemo(
    () =>
      (zone === Zone.Export
        ? exportChannels?.distributionChannelsExport
        : domesticChannels?.distributionChannelsDomestic) || [],
    [exportChannels, domesticChannels, zone]
  );

  const initChannel = useMemo(() => channels[0], [channels]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      soldTo: isCustomer
        ? `${listCompany?.find(e => e.soldToCode === company)?.soldToCode} - ${
            listCompany?.find(e => e.soldToCode === company)?.soldToName
          }`
        : "",
      companies:
        confirmationConpanies?.filterDomesticOrderConfirmationCompany?.map(
          e => e.code
        ) || [],
      channel: `${initChannel?.code}`,
      productGroup: "All",
      orderCreateDate: {
        gte: "",
        lte: ""
      },
      poNo: "",
      soNo: "",
      material: "",
      status: ""
    },
    onSubmit: () => {
      if (
        !formik.values.companies.length ||
        !formik.values.channel ||
        !formik.values.productGroup
      ) {
        const errorFields = [
          {
            fieldName: "companies",
            value: formik.values.companies?.length
          },
          {
            fieldName: "channel",
            value: formik.values.channel
          },
          {
            fieldName: "productGroup",
            value: formik.values.productGroup
          }
        ];
        errorFields.forEach(e => {
          if (!e.value) {
            formik.setFieldError(e.fieldName, "\t");
          }
        });
      } else {
        handleChangeFilter(formik.values);
      }
    }
  });

  const productGroupList = useMemo(() => {
    const result = getProductGroup(
      productGroup,
      intl.formatMessage({ defaultMessage: "All" })
    );
    if (result.length) {
      formik.setFieldValue("productGroup", "All");
    } else {
      formik.setFieldValue("productGroup", "");
    }
    return result;
  }, [productGroup]);

  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [isClean, setIsClean] = useState<boolean>(false);

  const ArrowIconComponent = useMemo(
    () => (isOpen ? KeyboardArrowUp : KeyboardArrowDown),
    [isOpen]
  );

  const handleCleanForm = useCallback(() => {
    setIsClean(true);
    formik.resetForm();
    handleChangeFilter({});
    setTimeout(() => setIsClean(false), 500);
  }, []);

  const [
    getMaterialCustomerOrder,
    { data: fetchMaterialCustomerOrder, loading: loadingMaterialCustomerOrder }
  ] = useFilterRequireAttentionMaterialLazyQuery();

  const [
    getMaterialAdminOrder,
    { data: fetchMaterialAdminOrder, loading: loadingMaterialAdminOrder }
  ] = useFilterRequireAttentionMaterialLazyQuery();

  const [
    getSoldToDataSuggestions,
    { data: fetchSoldToDataSuggestions, loading: loadingSoldToDataSuggestion }
  ] = useExportSoldTosLazyQuery();

  const formData = useMemo(
    () => [
      {
        title: intl.formatMessage({
          defaultMessage: "Sold to"
        }),
        name: "soldTo",
        placeholder: intl.formatMessage({
          defaultMessage: "ค้นหา Sold to..."
        }),
        type: !isCustomer
          ? FIELD_TYPE.SUGGESTION_INPUT
          : FIELD_TYPE.SINGLE_INPUT,
        disabled: isCustomer,
        maxLength: 35
      },
      {
        title: intl.formatMessage({ defaultMessage: "Company" }),
        name: "companies",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Company Name"
        }),
        type: FIELD_TYPE.MULTIPLE_SELECT,
        required: true,
        options: confirmationConpanies?.filterDomesticOrderConfirmationCompany?.map(
          e => ({
            label: `${e.code} - ${e.name}`,
            value: `${e.code}`
          })
        )
      },
      {
        title: intl.formatMessage({ defaultMessage: "Distribution Channel" }),
        name: "channel",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Channel"
        }),
        required: true,
        type: FIELD_TYPE.SINGLE_SELECT,
        options: channels.map(e => ({
          label: `${e.code} - ${e.name}`,
          value: `${e.code}`
        }))
      },
      {
        title: intl.formatMessage({ defaultMessage: "Product Group" }),
        name: "productGroup",
        required: true,
        placeholder: intl.formatMessage({ defaultMessage: "เลือก Order Type" }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: productGroupList
      },
      {
        title: intl.formatMessage({
          defaultMessage: "PO No."
        }),
        name: "poNo",
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุ PO No."
        }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 35
      },
      {
        title: intl.formatMessage({
          defaultMessage: "SO No."
        }),
        name: "soNo",
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุ SO No."
        }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 10
      },
      {
        title: intl.formatMessage({ defaultMessage: "Order Create Date" }),
        name: "orderCreateDate",
        type: FIELD_TYPE.DATEPICKER
      },
      {
        title: intl.formatMessage({ defaultMessage: "Status" }),
        name: "status",
        placeholder: intl.formatMessage({ defaultMessage: "เลือก Status" }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: [
          {
            label: "All",
            value: InquiryOrderConfirmationStatus.ALL
          },
          {
            label: "ยกเลิก",
            value: InquiryOrderConfirmationStatus.REJECT
          },
          {
            label: "รอผลิต",
            value: InquiryOrderConfirmationStatus.NON_CONFIRM
          },
          {
            label: "พร้อมส่ง",
            value: InquiryOrderConfirmationStatus.CONFIRM
          }
        ],
        maxLength: 10
      },
      {
        type: FIELD_TYPE.EMPTY_DIV
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Material Code / Description"
        }),
        subTitle: " (เลือกค้นหาได้ไม่เกิน 1 รายการ)",
        name: "material",
        placeholder: intl.formatMessage({
          defaultMessage: "Material Code / Description"
        }),
        type: FIELD_TYPE.SUGGESTION_INPUT
        // defaultValue:
        //   dataScgpMe?.scgpMe?.extendData?.defaultSalesOrganizations?.code
      }
    ],
    [confirmationConpanies, channels, productGroupList]
  );

  const setDataFormik = data => {
    formik.setValues(data);
  };

  return useMemo(
    () => ({
      formik,
      setDataFormik,
      isOpen,
      setIsOpen,
      isClean,
      setIsClean,
      ArrowIconComponent,
      handleCleanForm,
      formData,
      getMaterialCustomerOrder,
      fetchMaterialCustomerOrder,
      loadingMaterialCustomerOrder,
      fetchMaterialAdminOrder,
      getMaterialAdminOrder,
      loadingMaterialAdminOrder,
      getSoldToDataSuggestions,
      fetchSoldToDataSuggestions,
      loadingSoldToDataSuggestion,
      domesticChannels,
      exportChannels
    }),
    [
      formik,
      isOpen,
      setIsOpen,
      isClean,
      setIsClean,
      ArrowIconComponent,
      handleCleanForm,
      formData,
      getMaterialCustomerOrder,
      fetchMaterialCustomerOrder,
      loadingMaterialCustomerOrder,
      fetchMaterialAdminOrder,
      getMaterialAdminOrder,
      loadingMaterialAdminOrder,
      getSoldToDataSuggestions,
      fetchSoldToDataSuggestions,
      loadingSoldToDataSuggestion,
      domesticChannels,
      exportChannels
    ]
  );
};

export default useOrderFilterConfirmation;
